import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Flame, ChevronLeft, ArrowRight, Users } from 'lucide-react';
import { Button } from '../components/ui/button';
import Footer from '../components/Footer';
import { ministries } from '../data/ministries';

const Ministries = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#f8f4ee]">
      {/* Slim brand bar */}
      <header className="bg-[#0a1030] text-white">
        <div className="max-w-[1400px] mx-auto flex items-center justify-between px-6 md:px-12 py-5">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 group"
          >
            <Flame className="h-6 w-6" strokeWidth={2.5} />
            <span className="font-extrabold tracking-wide text-[14px] md:text-[15px]">
              APOSTOLIC CHRIST TABERNACLE
            </span>
          </button>
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-1 text-sm text-white/80 hover:text-white transition-colors"
          >
            <ChevronLeft className="h-4 w-4" /> Back to home
          </button>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden bg-gradient-to-b from-[#0a1030] via-[#111a4a] to-[#0a1030] text-white">
        <div className="absolute inset-0 opacity-30 bg-[radial-gradient(ellipse_at_top,rgba(200,185,138,0.35),transparent_60%)]" />
        <div className="relative max-w-[1000px] mx-auto text-center px-6 py-20 md:py-28">
          <p className="text-[12px] tracking-[0.3em] uppercase text-[#c8b98a] mb-5">
            Get Involved
          </p>
          <h1
            className="text-[46px] md:text-[76px] lg:text-[92px] leading-[1.02]"
            style={{ fontFamily: 'Fraunces, serif', fontWeight: 500 }}
          >
            Ministries
          </h1>
          <p className="mt-8 text-[17px] md:text-[19px] text-white/85 max-w-[720px] mx-auto leading-relaxed">
            Connection at Apostolic Christ Tabernacle (ACT) grows as you build
            relationships with our church family and find your place in serving
            God. We believe everyone has a purpose, and our ministries provide
            opportunities to grow in faith, serve others, and make a lasting
            impact. If you'd like to learn more about any of our ministries or
            how to get involved, we'd love to connect with you.
          </p>
        </div>
      </section>

      {/* Ministry cards */}
      <section className="py-20 md:py-28 px-4 md:px-6">
        <div className="max-w-[1200px] mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-10">
            {ministries.map((m) => {
              const Icon = m.icon;
              return (
                <Link
                  key={m.slug}
                  to={`/ministries/${m.slug}`}
                  className="group relative bg-white rounded-2xl overflow-hidden shadow-sm border border-black/5 hover:shadow-lg transition-all duration-300"
                >
                  <div className="relative aspect-[16/10] overflow-hidden">
                    <img
                      src={m.image}
                      alt={m.name}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent" />
                    <div className="absolute top-5 left-5 h-11 w-11 rounded-full bg-white/95 text-[#0a1030] flex items-center justify-center backdrop-blur">
                      <Icon className="h-5 w-5" strokeWidth={2} />
                    </div>
                  </div>
                  <div className="p-7 md:p-8">
                    <h3
                      className="text-[26px] md:text-[30px] text-[#0a1030] leading-tight"
                      style={{ fontFamily: 'Fraunces, serif', fontWeight: 500 }}
                    >
                      {m.name}
                    </h3>
                    <p className="mt-1 text-[13px] uppercase tracking-wider text-[#8a7a4a] font-semibold">
                      {m.tagline}
                    </p>
                    <p className="mt-4 text-[15px] text-[#4a4a4a] leading-relaxed">
                      {m.short}
                    </p>
                    <div className="mt-6 inline-flex items-center gap-1.5 text-[14px] font-semibold text-[#0a1030] group-hover:gap-3 transition-all">
                      Learn more <ArrowRight className="h-4 w-4" />
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>

          {/* CTA strip */}
          <div className="mt-20 md:mt-24 relative overflow-hidden rounded-3xl bg-[#0a1030] text-white p-10 md:p-14 text-center">
            <div className="absolute -top-16 -right-16 w-64 h-64 rounded-full bg-[#c8b98a]/10 blur-2xl" />
            <div className="absolute -bottom-16 -left-16 w-64 h-64 rounded-full bg-[#c8b98a]/10 blur-2xl" />
            <div className="relative">
              <div className="h-14 w-14 mx-auto rounded-full bg-white/10 border border-white/20 flex items-center justify-center mb-6">
                <Users className="h-6 w-6 text-[#c8b98a]" strokeWidth={2} />
              </div>
              <p className="text-[12px] tracking-[0.3em] uppercase text-[#c8b98a] mb-4">
                Ready to Connect?
              </p>
              <h3
                className="text-[30px] md:text-[42px] leading-tight max-w-[720px] mx-auto"
                style={{ fontFamily: 'Fraunces, serif', fontWeight: 500 }}
              >
                We'd love to help you find your place at ACT.
              </h3>
              <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
                <Button
                  asChild
                  className="rounded-full bg-[#e9e3d7] hover:bg-white text-[#0a1030] px-8 h-12 text-[14px] font-medium"
                >
                  <Link to="/plan-a-visit">Plan a visit</Link>
                </Button>
                <Button
                  asChild
                  variant="outline"
                  className="rounded-full border-white/30 bg-transparent text-white hover:bg-white/10 hover:text-white px-8 h-12 text-[14px] font-medium"
                >
                  <Link to="/contact">Get in touch</Link>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Ministries;
